import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Razorpaybutton from './Razorpaybutton';
import { useDispatch, useSelector } from 'react-redux';
import { createCheckout } from '../../../redux/slices/checkoutSlice';
import { clearCart } from '../../../redux/slices/cartSlice';
import axios from 'axios';

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cart, loading, error } = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  const { checkout, loading: checkoutLoading } = useSelector((state) => state.checkout);

  const [checkoutId, setCheckoutId] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [shippingAddress, setShipping] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    phone: "",
  });

  // Debug cart data
  useEffect(() => {
    console.log("=== CART DEBUG ===");
    console.log("Full cart object:", cart);
    console.log("Cart totalPrice:", cart?.totalPrice);
    console.log("Cart products:", cart?.products);
    
    if (cart?.products) {
      console.log("Individual product prices:");
      cart.products.forEach((product, index) => {
        console.log(`Product ${index}:`, {
          name: product.name,
          price: product.price,
          quantity: product.quantity,
          subtotal: product.price * product.quantity
        });
      });
      
      const manualTotal = cart.products.reduce((sum, item) => {
        return sum + (Number(item.price) * Number(item.quantity));
      }, 0);
      console.log("Manual calculated total:", manualTotal);
    }
  }, [cart]);

  // Calculate total price manually as fallback
  const calculateTotalPrice = () => {
    if (!cart || !cart.products || cart.products.length === 0) return 0;
    
    const total = cart.products.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const quantity = Number(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);
    
    console.log("Calculated total:", total);
    return total;
  };

  // Use manual calculation if cart.totalPrice is wrong
  const correctTotal = calculateTotalPrice();
  const displayTotal = correctTotal > 0 ? correctTotal : (cart?.totalPrice || 0);

  // Ensure cart is loaded before proceeding
useEffect(() => {
  if (!cart || !cart.products || cart.products.length === 0) {

    // Only redirect if payment isn't already done
    if (!checkoutId && !paymentProcessing) {
      navigate("/");
    }
  }
}, [cart, checkoutId, paymentProcessing, navigate]);


  const handleCreateCheckout = async (e) => {
    e.preventDefault();

    const requiredFields = ['firstName', 'lastName', 'address', 'city', 'state', 'postalCode', 'country', 'phone'];
    const isEmpty = requiredFields.some(field => !shippingAddress[field]?.trim());

    if (isEmpty) {
      alert('Please fill in all required fields');
      return;
    }

    if (!/^[\+]?[1-9][\d]{9,15}$/.test(shippingAddress.phone.replace(/[\s\-\(\)]/g, ''))) {
      alert('Please enter a valid phone number');
      return;
    }

    console.log("Creating checkout with correct total:", displayTotal);

    if (cart && cart.products && cart.products.length > 0) {
      try {
        const result = await dispatch(createCheckout({
          checkoutItems: cart.products,
          shippingAddress,
          paymentMethod: "Online",
          totalPrice: displayTotal,
        }));

        if (createCheckout.fulfilled.match(result)) {
          console.log('✅ Checkout created successfully:', result.payload);

          // i changed this
          setCheckoutId(result.payload?._id);

        } else {
          console.error('❌ Checkout creation failed:', result);
          alert('Failed to create checkout session');
        }
      } catch (error) {
        console.error('❌ Checkout creation error:', error);
        alert('Failed to create checkout session');
      }
    } else {
      alert('No items in cart');
    }
  };

  // NEW: Handle payment success with proper verification
  const handlePaymentSuccess = async (paymentDetails) => {
    console.log('🎉 Payment received from Razorpay, starting verification...');
    console.log('💳 Payment details:', paymentDetails);
    
    setPaymentProcessing(true);

    try {
      // Step 1: Verify payment signature on backend
      console.log('⏳ Step 1: Verifying payment signature...');
      const verificationResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/verify-payment`,
        {
          paymentId: paymentDetails.paymentId,
          orderId: paymentDetails.orderId,
          signature: paymentDetails.signature,
          amount: paymentDetails.amount,
          currency: paymentDetails.currency
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            'Content-Type': 'application/json'
          },
        }
      );

      if (!verificationResponse.data.success) {
        throw new Error('Payment verification failed');
      }

      console.log('✅ Step 1 completed - Payment verified successfully');

      // Step 2: Finalize checkout to create order
      console.log('⏳ Step 2: Finalizing checkout to create order...');
      const finalizeResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/checkout/${checkoutId}/finalize`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ Step 2 completed - Order created:', finalizeResponse.data);

      // Step 3: Clear cart
      console.log('⏳ Step 3: Clearing cart...');
      dispatch(clearCart());
      console.log('✅ Cart cleared from Redux');

      // Step 4: Navigate to confirmation page
      console.log('🚀 Navigating to order confirmation page...');
      
    setTimeout(() => {
  navigate("/order-confirmation", { 
    state: { 
      orderId: finalizeResponse.data._id,
      message: 'Order placed successfully!',
      verified: true
    } 
  });
}, 500);  // <-- THIS closes setTimeout properly



    } catch (error) {
      console.error('❌ Error in handlePaymentSuccess:', error);
      console.error('📋 Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      setPaymentProcessing(false);

      // Show appropriate error message
      if (error.response?.status === 400) {
        alert('Payment verification failed. If money was deducted, please contact support with Payment ID: ' + paymentDetails.paymentId);
      } else {
        alert('Error processing your order. Please contact support if payment was deducted. Payment ID: ' + paymentDetails.paymentId);
      }

      // Still navigate but with error state
      setTimeout(() => {
        navigate("/order-confirmation", { 
          state: { 
            error: 'Order verification failed. Please contact support.',
            paymentDetails: paymentDetails,
            contactSupport: true
          } 
        });
      }, 1000);
    }
  };

  // Handle payment errors
  const handlePaymentError = (error) => {
    console.error('❌ Payment error:', error);
    
    let errorMessage = 'Payment failed. ';
    
    switch(error.code) {
      case 'BAD_REQUEST_ERROR':
        errorMessage += 'Please check your payment details and try again.';
        break;
      case 'GATEWAY_ERROR':
        errorMessage += 'Payment gateway error. Please try again.';
        break;
      case 'NETWORK_ERROR':
        errorMessage += 'Network error. Please check your connection.';
        break;
      case 'SERVER_ERROR':
        errorMessage += 'Server error. Please try again later.';
        break;
      default:
        errorMessage += error.description || 'Please try again.';
    }
    
    alert(errorMessage);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading Cart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (!cart || !cart.products || cart.products.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-600">Your Cart is Empty</h2>
      </div>
    );
  }

  // Prepare customer details for Razorpay
  const customerDetails = {
    firstName: shippingAddress.firstName,
    lastName: shippingAddress.lastName,
    name: `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim(),
    email: user?.email || '',
    phone: shippingAddress.phone,
    mobile: shippingAddress.phone,
    address: shippingAddress.address,
    city: shippingAddress.city,
    state: shippingAddress.state,
    postalCode: shippingAddress.postalCode,
    country: shippingAddress.country
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-black mx-auto mb-4"></div>
            <h3 className="text-xl font-bold mb-2">Processing Payment...</h3>
            <p className="text-gray-600">Please do not close this window</p>
          </div>
        </div>
      )}

      <h1 className="text-3xl font-bold mb-8">Checkout</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Order Summary */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
          <div className="space-y-4">
            {cart.products.map((product, index) => (
              <div key={index} className="flex items-center space-x-4 border-b pb-4">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1">
                  <h3 className="font-medium">{product.name}</h3>
                  <p className="text-sm text-gray-600">
                    {product.size && `Size: ${product.size}`}
                    {product.size && product.color && ' | '}
                    {product.color && `Color: ${product.color}`}
                  </p>
                  <p className="text-sm">Quantity: {product.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">₹{product.price}</p>
                  <p className="text-xs text-gray-500">
                    Subtotal: ₹{(product.price * product.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg">Total:</span>
                <div className="text-right">
                  <p className="font-bold text-lg">₹{displayTotal.toFixed(2)}</p>
                  {cart.totalPrice !== displayTotal && (
                    <p className="text-xs text-red-500">
                      (Cart total: ₹{cart.totalPrice} - Recalculated)
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Information Form */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Shipping Information</h2>
          
          {!checkoutId ? (
            <form onSubmit={handleCreateCheckout} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="First Name *"
                  value={shippingAddress.firstName}
                  onChange={(e) => setShipping({...shippingAddress, firstName: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
                <input
                  type="text"
                  placeholder="Last Name *"
                  value={shippingAddress.lastName}
                  onChange={(e) => setShipping({...shippingAddress, lastName: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>
              
              <input
                type="text"
                placeholder="Address *"
                value={shippingAddress.address}
                onChange={(e) => setShipping({...shippingAddress, address: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                required
              />
              
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="City *"
                  value={shippingAddress.city}
                  onChange={(e) => setShipping({...shippingAddress, city: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
                <input
                  type="text"
                  placeholder="State *"
                  value={shippingAddress.state}
                  onChange={(e) => setShipping({...shippingAddress, state: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Postal Code *"
                  value={shippingAddress.postalCode}
                  onChange={(e) => setShipping({...shippingAddress, postalCode: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
                <input
                  type="text"
                  placeholder="Country *"
                  value={shippingAddress.country}
                  onChange={(e) => setShipping({...shippingAddress, country: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>
              
              <input
                type="tel"
                placeholder="Phone Number *"
                value={shippingAddress.phone}
                onChange={(e) => setShipping({...shippingAddress, phone: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                required
              />

              <button
                type="submit"
                disabled={checkoutLoading}
                className="w-full bg-black text-white py-3 px-4 rounded-md hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {checkoutLoading ? 'Creating Checkout...' : 'Proceed to Payment'}
              </button>
            </form>
          ) : (
            <div className="space-y-3 mb-6">
              <h3 className="font-semibold text-black">✓ Shipping Details Confirmed</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                <p><strong>Name:</strong> {shippingAddress.firstName} {shippingAddress.lastName}</p>
                <p><strong>Address:</strong> {shippingAddress.address}</p>
                <p><strong>City:</strong> {shippingAddress.city}, {shippingAddress.state}</p>
                <p><strong>Postal Code:</strong> {shippingAddress.postalCode}</p>
                <p><strong>Country:</strong> {shippingAddress.country}</p>
                <p><strong>Phone:</strong> {shippingAddress.phone}</p>
              </div>
            </div>
          )}

          {checkoutId && (
            <div className="mt-6">
              <h3 className="font-semibold mb-3">Complete Your Payment</h3>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-black">
                  <strong>Amount to Pay: ₹{displayTotal.toFixed(2)}</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Secure payment powered by Razorpay
                </p>
              </div>
              <Razorpaybutton 
                totalPrice={displayTotal}
                customerDetails={customerDetails}
                checkoutId={checkoutId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
              
              <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Secure payment with 256-bit encryption
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
